import { useState, SetStateAction } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import styled, { useTheme } from "styled-components";

import { usePermissions } from "@/hooks/usePermissions";
import { monolithMoment } from "@/utils/date-format";
import { StorageAPI } from "../storage.api";
import Loader from "@/components/Loader";
import Flyout, { FlyoutHeader } from "@/Monolith-UI/Flyout/Flyout";
import TaskButton from "@/Monolith-UI/TaskButton";
import { CopyStorageModal } from "../Modals/CopyStorageModal";
import { DeleteStorageModal } from "../Modals/DeleteStorageModal";
import { EditStorageModal } from "../Modals/EditStorageModal";
import { storageQueryKeys } from "../storage.query";
import { StorageFormData } from "../storage.types";
import { convertToEditableStorage } from "../storage.utils";
import { CustomAttribute } from "@/types";
import { ColumnProps } from "@/Monolith-UI/Table/types/Table";
import { KeyboardArrowRightOutlined as KeyboardArrowRightOutlinedIcon } from "@mui/icons-material";
import { KeyboardArrowLeftOutlined as KeyboardArrowLeftOutlinedIcon } from "@mui/icons-material";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import { StorageItem } from "../Tables/types";

interface StorageFlyoutProps {
  setIsFlyoutVisible: React.Dispatch<SetStateAction<boolean>>;
  setFlyoutData: React.Dispatch<SetStateAction<StorageItem | null>>;
  flyoutData?: StorageItem | null;
  columnState: ColumnProps<StorageItem>[];
  handleGetNextItem: (uuid: string) => void;
  handleGetPrevItem: (uuid: string) => void;
  handleRefresh: () => void;
  onDelete: (deleteInfo?: StorageItem) => void;
}

interface StorageFlyoutContentProps {
  className?: string;
  defaultInfo?: StorageItem | null;
  columnDefs: ColumnProps<StorageItem>[];
  onDelete: (deleteInfo?: StorageItem) => void;
  onEdit: () => void;
  onCopy: () => void;
}

interface Accumulator {
  [key: string]: CustomAttribute["value"];
}

export const StorageFlyout = ({
  setIsFlyoutVisible,
  setFlyoutData,
  flyoutData,
  columnState,
  handleGetNextItem,
  handleGetPrevItem,
  handleRefresh = () => {},
  onDelete = () => {},
}: StorageFlyoutProps) => {
  // will fix this any when we come up with the strategy to merge MUI and SC theme types
  const theme: any = useTheme();

  const handleClose = () => {
    setIsFlyoutVisible(false);
    setFlyoutData(null);
  };

  const onCopy = () => {
    handleClose();
    handleRefresh();
  };

  return (
    <Flyout
      onClose={() => {
        setIsFlyoutVisible(false);
        setFlyoutData(null);
      }}
    >
      <FlyoutHeader>
        <div style={{ width: "100%" }}>
          <h4
            className="flyout-title"
            style={{
              margin: "5px 0px",
              color: theme.palette.text.primary,
            }}
          >
            {flyoutData?.storage_number || "..."}
          </h4>
          <div
            style={{
              display: "flex",
              alignContent: "center",
              alignItems: "center",
            }}
          >
            <div
              style={{
                color: theme.palette.text.secondary,
                fontSize: 12,
              }}
            >
              Storage Item Details
            </div>
            <div
              style={{
                marginLeft: "auto",
                display: "flex",
                alignContent: "center",
                alignItems: "center",
              }}
            >
              <TaskButton
                onClick={() =>
                  handleGetPrevItem(flyoutData?.uuid ? flyoutData.uuid : "")
                }
                style={{
                  fontSize: 10,
                }}
                variant="outlined"
              >
                <KeyboardArrowLeftOutlinedIcon style={{ fontSize: 17 }} />
              </TaskButton>
              <TaskButton
                onClick={() =>
                  handleGetNextItem(flyoutData?.uuid ? flyoutData.uuid : "")
                }
                style={{
                  fontSize: 10,
                }}
                variant="outlined"
              >
                <KeyboardArrowRightOutlinedIcon style={{ fontSize: 17 }} />
              </TaskButton>
            </div>
          </div>
        </div>
      </FlyoutHeader>
      <FlyoutContent
        defaultInfo={flyoutData}
        columnDefs={columnState}
        onDelete={(deleteInfo?: StorageItem) => {
          handleClose();
          onDelete(deleteInfo);
        }}
        onEdit={handleRefresh}
        onCopy={onCopy}
      />
    </Flyout>
  );
};

const FlyoutContent = styled(
  ({
    className,
    defaultInfo,
    columnDefs,
    onDelete = () => {},
    onEdit = () => {},
    onCopy = () => {},
  }: StorageFlyoutContentProps) => {
    const theme: any = useTheme();
    const { case_id } = useParams();

    const { hasPermission, MONOLITH_PERMISSIONS } = usePermissions();
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [showCopyModal, setShowCopyModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const queryClient = useQueryClient();

    const { data: itemData } = useQuery<
      StorageItem[] | undefined,
      Error,
      StorageItem | undefined
    >({
      queryKey: storageQueryKeys.item(defaultInfo?.uuid),
      queryFn: () => StorageAPI.getStorage({ uuid: defaultInfo?.uuid }),
      initialData: defaultInfo ? [defaultInfo] : [],
      select: (res) => {
        if (res && res.length > 0) {
          return res[0];
        }
        return undefined;
      },
    });

    const title = document.querySelector(".flyout-title") as HTMLElement;
    if (title)
      title.innerText =
        itemData?.storage_number || defaultInfo?.storage_number || "";

    const handleDelete = () => {
      setShowDeleteModal(true);
    };

    const handleCopy = () => {
      setShowCopyModal(true);
    };

    const _onEdit = () => {
      onEdit();
      queryClient.refetchQueries({
        queryKey: storageQueryKeys.item(defaultInfo?.uuid),
      });
    };

    const dataFieldIgnoreList = ["notes"];

    const data = {
      ...itemData,
      ...(itemData?.custom_attributes?.reduce<Accumulator>(
        (acc, attr: CustomAttribute) => {
          if (attr.value === null || attr.value === undefined) return acc;
          acc[`custom_field_${attr.field_id}`] = attr.value;
          return acc;
        },
        {}
      ) || {}),
    } as Record<string, string | number | string[] | undefined>;

    const detail = (
      <div className="detail-container">
        {/* MAIN DATA */}
        {columnDefs
          .filter(
            (c: ColumnProps<StorageItem>) =>
              !dataFieldIgnoreList.includes(c.dataField)
          )
          .filter((c: ColumnProps<StorageItem>) =>
            // Gate on "has a value", not truthiness — a numeric custom field
            // can legitimately hold 0.
            c.dataField.includes("custom_field_")
              ? data[c.dataField] !== undefined &&
                data[c.dataField] !== null &&
                data[c.dataField] !== ""
              : true
          )
          .map((c: ColumnProps<StorageItem>) => {
            return (
              <div className="detail-item" key={c.dataField}>
                <div className="detail-label">{c.caption}</div>
                {c.render ? (
                  c.render(data, { onChange: _onEdit })
                ) : c.dataType === "date" ? (
                  !!data[c.dataField] ? (
                    <>
                      {monolithMoment({
                        // date columns never hold array values
                        timestamp: data[c.dataField] as string | number,
                        includeTime: true,
                      })}
                    </>
                  ) : null
                ) : (
                  <div className="detail-value">{data[c.dataField]}</div>
                )}
              </div>
            );
          })}
      </div>
    );

    return (
      <div className={className}>
        {!!itemData && (
          <>
            <div className="action-menu">
              {hasPermission(MONOLITH_PERMISSIONS.STORAGE_CREATE) && (
                <div className="action-menu-item" onClick={handleCopy}>
                  <ContentCopyOutlinedIcon
                    style={{
                      color: theme.palette.primary.main,
                    }}
                  />
                  <div className="action-menu-label">Copy</div>
                </div>
              )}
              {hasPermission(MONOLITH_PERMISSIONS.STORAGE_UPDATE) && (
                <div
                  className="action-menu-item"
                  onClick={() => setShowEditModal(true)}
                >
                  <EditOutlinedIcon
                    style={{
                      color: theme.palette.primary.main,
                    }}
                  />
                  <div className="action-menu-label">Edit</div>
                </div>
              )}
              {hasPermission(MONOLITH_PERMISSIONS.STORAGE_DELETE) && (
                <>
                  <div className="action-menu-item" onClick={handleDelete}>
                    <DeleteOutlineOutlinedIcon
                      style={{
                        color: theme.palette.error.main,
                      }}
                    />
                    <div className="action-menu-label">Delete</div>
                  </div>
                </>
              )}
            </div>
            {detail}
            <div className="detail-label" style={{ marginTop: 30 }}>
              Description
            </div>
            <div style={{ position: "relative" }}>
              <div className="item-description">{itemData.notes}</div>
            </div>
          </>
        )}
        {!itemData && <Loader message={"Loading..."} />}
        <DeleteStorageModal
          open={showDeleteModal}
          onClose={() => setShowDeleteModal(false)}
          onDelete={() => {
            setShowDeleteModal(false);
            onDelete(itemData);
          }}
          storageInfo={itemData ?? null}
        />
        <CopyStorageModal
          show={showCopyModal}
          onClose={() => setShowCopyModal(false)}
          onCancel={() => setShowCopyModal(false)}
          onSubmit={() => {
            // the modal closes itself via its own onClose
            onCopy();
          }}
          copyFromData={itemData as unknown as StorageFormData}
          caseInfo={{ case_id: case_id ? parseInt(case_id) : null }}
        />
        {showEditModal && itemData && (
          <EditStorageModal
            open={showEditModal}
            handleClose={() => setShowEditModal(false)}
            onEditSubmit={() => {
              _onEdit();
            }}
            defaultStorageInfo={convertToEditableStorage(itemData)}
          />
        )}
      </div>
    );
  }
)`
  padding: 20px;
  & {
    max-height: calc(100vh - 100px);
    overflow-y: auto;
    margin-right: 5px;
  }
  & .action-menu {
    display: flex;
    align-items: center;
    align-content: center;
    margin-bottom: 15px;
    margin-right: 10px;
  }
  & .action-menu-item {
    display: flex;
    align-items: center;
    align-content: center;
    margin-right: 12px;
    cursor: pointer;
    border-radius: 5px;
    font-size: 12px;
    &:hover {
      text-decoration: underline;
    }
    & svg {
      font-size: 15px;
      margin-right: 5px;
    }
  }
  & .detail-item {
    display: flex;
    align-items: center;
    align-content: center;
    margin: 10px 0px;
    font-size: 12px;
  }
  & .detail-label {
    color: ${({ theme }) => theme.palette.text.secondary};
    margin-right: 10px;
    min-width: 150px;
  }
  & .detail-value {
    color: ${({ theme }) => theme.palette.text.primary};
  }
  & .item-description {
    position: relative;
    resize: none;
    margin-top: 10px;
    font-size: 12px;
    font-family: ${({ theme }) => theme.typography.fontFamily};
    color: ${({ theme }) => theme.palette.text.primary};
    background-color: transparent;
    white-space: pre-wrap;
    width: 100%;
    height: fit-content;
    max-height: 30%;
    border: 1px solid transparent;
    border-radius: 5px;
    padding: 10px;
    transition: border 0.2s ease-in-out;
    outline: none;
    &:hover {
      border: 1px solid ${({ theme }) => theme.palette.divider};
    }
    &:focus {
      border: 1px solid ${({ theme }) => theme.palette.primary.main};
    }
  }
  & .item-description-placeholder {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    color: ${({ theme }) => theme.palette.divider};
    display: flex;
    alignitems: center;
    fontsize: 12px;
    padding: 10px;
  }
`;
